import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Calendar, CheckCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useLanguage, t, API } from './Common';
import LanguageSwitcher from '@/components/LanguageSwitcher';

const BookAppointmentPage = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const { assessment, email, name, payLater, fromOtherDevice } = location.state || {};
  const [calendlyUrl, setCalendlyUrl] = useState('');
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    // Allow access if coming from "Other" device selection OR if there's an assessment
    if (!assessment && !fromOtherDevice) {
      navigate('/');
      return;
    }
    fetchSettings();
  }, [assessment, fromOtherDevice]);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setSettings(response.data);
      if (response.data.calendly_url) {
        setCalendlyUrl(response.data.calendly_url);
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    }
  };

  return (
    <div className="min-h-screen p-3 md:p-6">
      <div className="scan-line" />
      <LanguageSwitcher />
      <div className="max-w-4xl mx-auto py-4 md:py-12">
        {/* Header section for "Other Device" flow - compatto su mobile */}
        {fromOtherDevice && (
          <div className="glass-panel rounded-sm p-4 md:p-8 text-center border border-secondary mb-4 md:mb-8">
            <h1 className="text-xl md:text-3xl font-bold tracking-tight mb-2">
              {language === 'it' ? 'Prenota la Tua Consulenza' : 'Book Your Consultation'}
            </h1>
            <p className="text-sm md:text-base text-muted-foreground">
              {language === 'it' 
                ? `Tariffa: €${settings?.consultation_price?.toFixed(2) || '400.00'}` 
                : `Fee: €${settings?.consultation_price?.toFixed(2) || '400.00'}`}
            </p>
          </div>
        )}

        {payLater && (
          <div className="glass-panel rounded-sm p-4 md:p-8 text-center border-2 border-premium premium-glow mb-4 md:mb-8">
            <CheckCircle className="w-10 h-10 md:w-16 md:h-16 text-premium mx-auto mb-2 md:mb-4" data-testid="booking-confirmed-icon" />
            <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-4">
              {language === 'it' ? 'Prenotazione Confermata' : 'Booking Confirmed'}
            </h1>
            <div className="space-y-2 text-muted-foreground">
              <p className="flex items-center justify-center gap-2">
                <Mail className="w-4 h-4" />
                {email}
              </p>
              {name && <p>{name}</p>}
              <p className="text-premium font-semibold mt-4">
                {language === 'it' 
                  ? 'Pagamento durante l\'incontro: €400.00' 
                  : 'Payment during meeting: €400.00'}
              </p>
            </div>
          </div>
        )}

        {calendlyUrl ? (
          <div className="glass-panel rounded-sm p-3 md:p-8 lg:p-12">
            <h2 className="text-lg md:text-2xl lg:text-3xl font-semibold mb-3 flex items-center justify-center gap-2">
              <Calendar className="w-5 h-5 md:w-8 md:h-8 text-primary" />
              {language === 'it' ? 'Seleziona Data e Ora' : 'Select Date and Time'}
            </h2>
            <p className="text-xs md:text-base text-muted-foreground mb-3 md:mb-6 text-center">
              {language === 'it'
                ? 'Scegli un orario conveniente per la tua analisi di sicurezza dello smartphone.'
                : 'Choose a convenient time for your smartphone security analysis.'}
            </p>
            
            {/* Calendly iframe - quasi full screen su mobile */}
            <div className="bg-black/20 rounded-sm border border-white/10 overflow-hidden h-[70vh] md:h-[65vh] min-h-[550px]">
              <iframe
                src={calendlyUrl}
                width="100%"
                height="100%"
                frameBorder="0"
                data-testid="calendly-widget"
                title="Schedule Appointment"
              />
            </div>
            
            {fromOtherDevice && (
              <div className="mt-6">
                <Button
                  variant="outline"
                  onClick={() => navigate('/device-selection')}
                  data-testid="back-to-device-selection"
                  className="w-full border-white/20 hover:border-primary/50 rounded-none uppercase tracking-widest font-mono text-xs"
                >
                  {language === 'it' ? 'Indietro' : 'Back'}
                </Button>
              </div>
            )}
          </div>
        ) : (
          <div className="glass-panel rounded-sm p-8 text-center">
            <p className="text-muted-foreground mb-4">
              {language === 'it'
                ? 'Per prenotare il tuo appuntamento, ti preghiamo di contattare Smart Info direttamente.'
                : 'To book your appointment, please contact Smart Info directly.'}
            </p>
            {email && <p className="text-lg font-semibold mb-6">{email}</p>}
            <Button
              onClick={() => navigate(fromOtherDevice ? '/device-selection' : '/')}
              data-testid="return-home-button"
              className="rounded-none border border-primary bg-primary/10 text-primary hover:bg-primary hover:text-black transition-all duration-300 uppercase tracking-widest font-mono text-sm"
            >
              {language === 'it' ? (fromOtherDevice ? 'Indietro' : 'Torna alla Home') : (fromOtherDevice ? 'Back' : 'Return Home')}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BookAppointmentPage;
